import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Loader2, ArrowRight } from "lucide-react";
import { api } from "../../lib/api";

/**
 * WeekendRegionPoll — anonymous landing-page poll.
 *
 *   Question: "Where are you off to this weekend?"
 *   6 region buttons. Live vote bars + percentages visible to everyone
 *   (no signup required to see counts — that's the whole hook).
 *   Tapping a region casts a vote AND routes to /browse?region=<region>
 *   so the user immediately sees the matching campsites.
 *
 * Voter dedup is server-side via IP+UA hash for anon, user_id for signed-in.
 * Same browser can change their vote — bar just updates in place.
 *
 * Six colour-coded buttons matching the variety reels palette so the brand
 * carousel feels continuous from Instagram → landing.
 */

// Visual treatment per option key. Every poll's options will fall back to a
// vibrant rotating gradient if no specific mapping is found, so the row stays
// colourful even when a poll uses keys we don't have a custom palette for.
const FALLBACK_GRADS = [
  { grad: "linear-gradient(135deg,#0ea5e9 0%,#3b82f6 100%)", accent: "#fde047" },
  { grad: "linear-gradient(135deg,#fb923c 0%,#facc15 100%)", accent: "#ffffff" },
  { grad: "linear-gradient(135deg,#7c3aed 0%,#ec4899 100%)", accent: "#fde047" },
  { grad: "linear-gradient(135deg,#16a34a 0%,#0d9488 100%)", accent: "#fde047" },
  { grad: "linear-gradient(135deg,#dc2626 0%,#7c2d12 100%)", accent: "#fde047" },
  { grad: "linear-gradient(135deg,#0891b2 0%,#16a34a 100%)", accent: "#ffffff" },
];
const REGION_STYLES = {
  lake_district: FALLBACK_GRADS[0], cornwall: FALLBACK_GRADS[1],
  scotland: FALLBACK_GRADS[2], wales: FALLBACK_GRADS[3],
  peak_district: FALLBACK_GRADS[4], yorkshire: FALLBACK_GRADS[5],
  // iter121 — expanded "Where will your next trip be?" poll
  new_forest:     { grad: "linear-gradient(135deg,#15803d 0%,#84cc16 100%)", accent: "#fde047" },
  devon:          { grad: "linear-gradient(135deg,#0891b2 0%,#0ea5e9 100%)", accent: "#ffffff" },
  pembrokeshire:  { grad: "linear-gradient(135deg,#0d9488 0%,#22d3ee 100%)", accent: "#fde047" },
  northumberland: { grad: "linear-gradient(135deg,#1e293b 0%,#475569 100%)", accent: "#fde047" },
  norfolk:        { grad: "linear-gradient(135deg,#a16207 0%,#eab308 100%)", accent: "#ffffff" },
  anywhere:       { grad: "linear-gradient(135deg,#0e0e0e 0%,#ff4500 100%)", accent: "#fde047" },
};

// Today's poll endpoint — auto-rotates daily, so the landing page surfaces
// fresh content all week without any deploy.
const POLL_TODAY = "today";

export default function WeekendRegionPoll() {
  const [poll, setPoll] = useState(null);
  const [voting, setVoting] = useState(null); // option_key being submitted
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/polls/${POLL_TODAY}`);
      setPoll(data);
    } catch {
      setError("Couldn't load poll. Refresh in a sec.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const vote = async (opt) => {
    if (voting) return;
    setVoting(opt.key);
    try {
      const { data } = await api.post(`/polls/${poll.poll_id}/vote`, { option: opt.key });
      setPoll(data);
      window.dispatchEvent(new CustomEvent("cc:presence-bump", { detail: { source: "poll" } }));
      setTimeout(() => {
        const param = poll.filter_param || "region";
        if (opt.browse_filter) {
          navigate(`/browse?${encodeURIComponent(param)}=${encodeURIComponent(opt.browse_filter)}`);
        } else {
          // iter121 — "Anywhere · surprise me" routes to /browse with no filter
          // so every poll option still ends the session with the user landing
          // on a campsite list (no dead-end taps).
          navigate("/browse");
        }
      }, 650);
    } catch {
      setError("Couldn't save your vote. Try once more.");
      setVoting(null);
    }
  };

  if (!poll && !error) {
    return (
      <section className="border-b hairline bg-white" data-testid="weekend-poll-loading">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 flex items-center justify-center text-[#888] text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading today's poll…
        </div>
      </section>
    );
  }

  if (error || !poll) {
    return (
      <section className="border-b hairline bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 text-sm text-[#888] text-center">{error}</div>
      </section>
    );
  }

  // Sort options by current count desc so the leader is always visible at the top
  // — but on desktop we still render in a 3-col grid that preserves the natural
  // reading order. Sort is for the "ranked" bar list on mobile.
  const ordered = [...poll.options].sort((a, b) => b.count - a.count);
  const max = Math.max(1, ...poll.options.map((o) => o.count));

  return (
    <section
      className="border-b hairline-strong relative overflow-hidden"
      style={{ background: "linear-gradient(135deg,#fff7ed 0%,#fef3c7 50%,#dcfce7 100%)" }}
      data-testid="weekend-poll"
    >
      {/* Decorative grain so the warm-cream gradient has texture not flatness */}
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(rgba(0,0,0,0.06) 1px, transparent 1px)",
          backgroundSize: "18px 18px",
        }}
      />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 sm:py-20 relative">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="eyebrow text-[#ff4500] mb-2 inline-flex items-center gap-2">
              <MapPin className="w-3 h-3" /> Reader poll · live results
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.02] max-w-3xl">
              {poll.question}
            </h2>
            <p className="text-sm sm:text-base text-[#444] mt-3 max-w-xl leading-relaxed">
              <strong>Pick one to unlock browse</strong> — we'll show you matching sites instantly. No login, one vote per browser, change your pick anytime.
              {poll.my_choice && (
                <span className="block mt-1 mono text-[11px] text-[#16a34a]">
                  ✓ Your vote: {poll.options.find((o) => o.key === poll.my_choice)?.label}. Tap any other region to change.
                </span>
              )}
            </p>
          </div>
          <div className="mono text-xs sm:text-sm text-[#666] bg-white border hairline px-3 py-2" data-testid="weekend-poll-total">
            <span className="font-display text-2xl text-[#ff4500] block leading-none">
              {poll.total.toLocaleString("en-GB")}
            </span>
            <span className="text-[10px] uppercase tracking-wider">votes so far</span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {ordered.map((opt, idx) => {
            const style = REGION_STYLES[opt.key] || FALLBACK_GRADS[idx % FALLBACK_GRADS.length];
            const isMine = poll.my_choice === opt.key;
            const isVoting = voting === opt.key;
            const widthPct = max > 0 ? (opt.count / max) * 100 : 0;
            return (
              <button
                key={opt.key}
                onClick={() => vote(opt)}
                disabled={!!voting}
                className={`relative overflow-hidden group text-left transition-transform hover:-translate-y-0.5 active:translate-y-0 disabled:cursor-wait border-2 ${isMine ? "border-[#111] shadow-[4px_4px_0_0_#111]" : "border-transparent hover:border-[#111]"}`}
                style={{ background: style.grad, color: "white" }}
                data-testid={`weekend-poll-${opt.key}`}
              >
                {/* Animated fill bar — % of max */}
                <div
                  className="absolute inset-y-0 left-0 transition-[width] duration-700 ease-out pointer-events-none"
                  style={{
                    width: `${widthPct}%`,
                    background: "rgba(255,255,255,0.18)",
                    mixBlendMode: "overlay",
                  }}
                />
                <div className="relative p-4 sm:p-5 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10px] mono uppercase tracking-[0.25em] opacity-80 mb-1">
                      #{idx + 1} {isMine && "· your pick"}
                    </div>
                    <div className="font-display text-xl sm:text-2xl leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]">
                      {opt.label}
                    </div>
                    <div className="mt-2 text-[11px] mono tracking-wider opacity-90">
                      <span className="font-display text-lg" style={{ color: style.accent }}>
                        {opt.count.toLocaleString("en-GB")}
                      </span>
                      <span className="mx-1.5 opacity-60">·</span>
                      <span>{opt.pct}%</span>
                    </div>
                  </div>
                  <div className="shrink-0 self-center">
                    {isVoting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <ArrowRight className="w-5 h-5 opacity-80 group-hover:translate-x-1 transition-transform" />
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <p className="text-[11px] mono text-[#888] mt-6 text-center">
          One vote per browser. Change your mind any time — just tap a different region.
        </p>
      </div>
    </section>
  );
}
